/**
 * Analytics Utility
 * Centralized analytics tracking for user events
 * Ready for Google Analytics 4 integration
 */

interface AnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
  userId?: string;
}

class Analytics {
  private static instance: Analytics;
  private initialized = false;
  private readonly isDevelopment = process.env.NODE_ENV === 'development';
  
  private constructor() {}
  
  public static getInstance(): Analytics {
    if (!Analytics.instance) {
      Analytics.instance = new Analytics();
    }
    return Analytics.instance;
  }
  
  /**
   * Initialize analytics with tracking ID
   * Google Analytics 4 integration enabled
   */
  public init(trackingId?: string): void {
    if (this.initialized) {
      return;
    }
    
    // Use tracking ID from environment or parameter
    const gaId = trackingId || process.env.REACT_APP_GA_TRACKING_ID;
    
    if (!gaId) {
      console.warn('[Analytics] No tracking ID provided');
      return;
    }
    
    try {
      // Initialize gtag.js for Google Analytics 4
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      document.head.appendChild(script);
      
      const win = globalThis as any;
      win.dataLayer = win.dataLayer || [];
      const gtag = (...args: any[]) => {
        win.dataLayer.push(args);
      };
      gtag('js', new Date());
      gtag('config', gaId);
      
      // Make gtag available globally
      win.gtag = gtag;
      
      this.initialized = true;
      console.log('[Analytics] Initialized successfully with ID:', gaId);
    } catch (error) {
      console.error('[Analytics] Initialization failed:', error);
    }
  }
  
  /**
   * Track page view
   */
  public pageView(path: string, title?: string): void {
    this.logDev('PageView', { path, title });
    
    if (!this.initialized) {
      return;
    }
    
    try {
      const gtag = (globalThis as any).gtag;
      if (gtag) {
        gtag('event', 'page_view', {
          page_path: path,
          page_title: title || document.title
        });
      }
    } catch (error) {
      console.error('[Analytics] PageView tracking failed:', error);
    }
  }
  
  /**
   * Track custom event
   */
  public event({ category, action, label, value, userId }: AnalyticsEvent): void {
    this.logDev('Event', { category, action, label, value, userId });
    
    if (!this.initialized) {
      return;
    }
    
    try {
      const gtag = (globalThis as any).gtag;
      if (gtag) {
        gtag('event', action, {
          event_category: category,
          event_label: label,
          value: value,
          user_id: userId
        });
      }
    } catch (error) {
      console.error('[Analytics] Event tracking failed:', error);
    }
  }
  
  /**
   * Track typing test completion
   */
  public trackTest(mode: string, wpm: number, accuracy: number, duration: number): void {
    this.event({
      category: 'Typing Test',
      action: 'Complete',
      label: mode,
      value: Math.round(wpm)
    });
    
    // Track detailed metrics
    this.event({
      category: 'Performance',
      action: 'WPM',
      label: mode,
      value: Math.round(wpm)
    });
    
    this.event({
      category: 'Performance',
      action: 'Accuracy',
      label: mode,
      value: Math.round(accuracy)
    });
  }
  
  /**
   * Track battle events
   */
  public trackBattle(action: 'Create' | 'Join' | 'Complete' | 'Leave', roomType: string): void {
    this.event({
      category: 'Battle',
      action,
      label: roomType
    });
  }
  
  /**
   * Track premium conversions
   */
  public trackPremium(action: 'View' | 'Purchase' | 'Cancel', method?: string): void {
    this.event({
      category: 'Premium',
      action,
      label: method
    });
  }
  
  /**
   * Track user authentication
   */
  public trackAuth(action: 'Login' | 'Logout' | 'Register', method?: string): void {
    this.event({
      category: 'Authentication',
      action,
      label: method
    });
  }
  
  /**
   * Track errors
   */
  public trackError(errorType: string, errorMessage: string): void {
    this.event({
      category: 'Error',
      action: errorType,
      label: errorMessage
    });
  }
  
  /**
   * Set user ID for tracking
   */
  public setUserId(userId: string | null): void {
    this.logDev('Set User ID', { userId });
    
    if (!this.initialized || !userId) return;
    
    try {
      const gtag = (globalThis as any).gtag;
      if (gtag) {
        gtag('set', { user_id: userId });
      }
    } catch (error) {
      console.error('[Analytics] Set user ID failed:', error);
    }
  }
  
  /**
   * Log events in development mode
   */
  private logDev(type: string, data: any): void {
    if (this.isDevelopment) {
      console.log(`[Analytics ${type}]`, data);
    }
  }
}

// Export singleton instance
export const analytics = Analytics.getInstance();

// Export convenience methods
export const trackPageView = (path: string, title?: string) => 
  analytics.pageView(path, title);

export const trackEvent = (event: AnalyticsEvent) => 
  analytics.event(event);

export const trackTest = (mode: string, wpm: number, accuracy: number, duration: number) =>
  analytics.trackTest(mode, wpm, accuracy, duration);

export const trackBattle = (action: 'Create' | 'Join' | 'Complete' | 'Leave', roomType: string) =>
  analytics.trackBattle(action, roomType);

export const trackPremium = (action: 'View' | 'Purchase' | 'Cancel', method?: string) =>
  analytics.trackPremium(action, method);

export const trackAuth = (action: 'Login' | 'Logout' | 'Register', method?: string) =>
  analytics.trackAuth(action, method);

export const trackError = (errorType: string, errorMessage: string) =>
  analytics.trackError(errorType, errorMessage);

export const setUserId = (userId: string | null) =>
  analytics.setUserId(userId);
